import { z } from 'zod'
import type { ModelPayload, PricingRules } from './types.js'

export const BLOCK_KEYS = ['market', 'structure', 'client', 'deal'] as const

const blockKey = z.enum(BLOCK_KEYS)
const finite = z.number().finite()

export const effectSchema = z.object({
  level: z.string().min(1),
  value: finite,
})

export const driverSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  nameDe: z.string().optional(),
  block: blockKey,
  kind: z.enum(['relative', 'absolute']),
  grain: z.enum(['product', 'customer', 'market', 'deal']),
  effects: z.array(effectSchema).superRefine((effects, ctx) => {
    const seen = new Set<string>()
    for (const e of effects) {
      if (seen.has(e.level)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate level "${e.level}"` })
      }
      seen.add(e.level)
    }
  }),
})

export const authorityLimitSchema = z.object({
  ref: z.enum(['ask', 'target', 'floor', 'cost']),
  unit: z.enum(['pct', 'eur', 'drivers']),
  below: z.number().finite(),
})
export const authorityLimitsSchema = z.object({
  rep: authorityLimitSchema,
  supervisor: authorityLimitSchema,
  head: authorityLimitSchema.nullable(),
})

const policySchema = z.object({
  concessionRate: finite.min(0),
  capRate: finite.min(0).nullable(),
  floorIncludesBlocks: z.array(blockKey).default(['market']),
  authority: z
    .object({
      repFloor: z.enum(['target', 'ask']).default('target'),
      hardFloor: z.enum(['floor', 'blocked']).default('floor'),
      limits: authorityLimitsSchema.optional(),
    })
    .default({ repFloor: 'target', hardFloor: 'floor' }),
  costMoveBasis: z.enum(['price', 'cost']).default('cost'),
})

const numberMap = z.record(z.string(), finite)

export const modelPayloadSchema = z
  .object({
    schemaVersion: z.literal(1),
    publishedAt: z.string().datetime({ offset: true }),
    publishedBy: z.string().min(1),
    note: z.string().default(''),
    provenance: z
      .object({
        source: z.enum(['imported', 'hand_edited']),
        parentVersionId: z.string().optional(),
        editedEffects: z
          .array(
            z.object({
              driverId: z.string(),
              level: z.string(),
              from: finite.nullable(),
              to: finite.nullable(),
            }),
          )
          .optional(),
        editedOther: z
          .array(z.object({ path: z.string(), from: z.unknown(), to: z.unknown() }))
          .optional(),
      })
      .superRefine((p, ctx) => {
        if (p.source === 'hand_edited' && !p.parentVersionId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'hand_edited provenance must name parentVersionId',
            path: ['parentVersionId'],
          })
        }
      }),
    basis: z.enum(['ex_works', 'delivered']),
    currency: z.string().length(3),
    intercept: finite.default(0),
    absBase: finite.default(0),
    blocks: z
      .array(z.object({ key: blockKey, name: z.string().min(1), nameDe: z.string().optional() }))
      .superRefine((blocks, ctx) => {
        const keys = blocks.map((b) => b.key)
        const expected = [...BLOCK_KEYS]
        if (keys.length !== expected.length || expected.some((k) => !keys.includes(k))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `blocks must contain exactly the keys ${expected.join(', ')}`,
          })
        }
      }),
    drivers: z.array(driverSchema).superRefine((drivers, ctx) => {
      const seen = new Set<string>()
      for (const d of drivers) {
        if (seen.has(d.id)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate driver id "${d.id}"` })
        }
        seen.add(d.id)
      }
    }),
    /** index bases are derived per article from the months its cost was measured in; an old
     *  payload's empty map is dropped, one with values is refused */
    indexBase: numberMap
      .optional()
      .superRefine((o, ctx) => {
        if (o && Object.keys(o).length > 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'index bases are no longer part of the model; they follow from the months each article’s cost was measured in' })
        }
      }),
    /**
     * Cost overrides moved to the product data (fixed_cost, cost_factor). Older payloads may
     * still carry the key; an empty one is dropped, one with values is refused so none are lost.
     */
    costOverrides: z
      .record(z.string(), z.unknown())
      .optional()
      .superRefine((o, ctx) => {
        const filled = o && Object.values(o).some((v) => v && typeof v === 'object' && Object.keys(v).length > 0)
        if (filled) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'cost overrides are no longer part of the model; set fixed_cost or cost_factor in the products data instead',
          })
        }
      }),
    // uplift and policy are the tenant's rules now, set in their own sections; a model file that
    // still carries them is refused rather than having them silently ignored
    uplift: z.unknown().optional(),
    policy: z.unknown().optional(),
    policyByCategory: z.unknown().optional(),
  })
  .strict()
  .superRefine((p, ctx) => {
    for (const k of ['uplift', 'policy', 'policyByCategory'] as const) {
      if (p[k] !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [k],
          message: `${k} is no longer part of the model; the uplift and the governance are set in their own sections`,
        })
      }
    }
  })

export type ModelPayloadInput = z.input<typeof modelPayloadSchema>

/** Parse and validate a payload. Throws a ZodError on failure; never returns a partial model. */
export function parseModelPayload(input: unknown): ModelPayload {
  return withoutLegacy(modelPayloadSchema.parse(input))
}

export function safeParseModelPayload(input: unknown) {
  const r = modelPayloadSchema.safeParse(input)
  return r.success
    ? { success: true as const, data: withoutLegacy(r.data) }
    : { success: false as const, error: r.error }
}

function withoutLegacy(p: z.output<typeof modelPayloadSchema>): ModelPayload {
  const { costOverrides: _dropped, uplift: _u, policy: _p, policyByCategory: _c, indexBase: _i, ...rest } = p
  return rest as ModelPayload
}

const upliftSchema = z
  .object({
    base: finite.optional(),
    cat: numberMap.optional(),
    sub: numberMap.optional(),
  })
  .strict()

export const policyOverrideSchema = z.object({
  concessionRate: finite.min(0).optional(),
  capRate: finite.min(0).nullable().optional(),
  floorIncludesBlocks: z.array(blockKey).optional(),
  authority: z
    .object({ repFloor: z.enum(['target', 'ask']), hardFloor: z.enum(['floor', 'blocked']), limits: authorityLimitsSchema.optional() })
    .optional(),
  costMoveBasis: z.enum(['price', 'cost']).optional(),
})

export const pricingRulesSchema = z
  .object({
    uplift: upliftSchema.default({}),
    policy: policySchema,
    policyByCategory: z.record(z.string(), policyOverrideSchema).optional(),
  })
  .strict()

export { upliftSchema, policySchema }

export function safeParsePricingRules(input: unknown) {
  const r = pricingRulesSchema.safeParse(input)
  return r.success ? { success: true as const, data: r.data as PricingRules } : { success: false as const, error: r.error }
}
